import { useCallback, useMemo, useReducer } from 'react'
import PropTypes from 'prop-types'
import ModalPortal from '../components/ModalPortal.jsx'
import { useUserPreferences } from '../hooks/useUserPreferences.js'

/**
 * Notification Settings Modal component - Firebase v11+ & ES2022+ Optimized
 * Features: Real-time preference sync, minimal re-renders, DRY principles
 * Now wrapped in a modal for better UX integration
 */

// Optimized state reducer - prevents multiple state updates and re-renders
const messageReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SUCCESS': return { success: action.payload, error: '', actionLoading: state.actionLoading }
    case 'SET_ERROR': return { error: action.payload, success: '', actionLoading: state.actionLoading }
    case 'SET_LOADING': return { ...state, actionLoading: action.payload }
    case 'CLEAR': return { success: '', error: '', actionLoading: state.actionLoading }
    default: return state
  }
}

// Memoized notification options - prevents re-creation on every render
const NOTIFICATION_OPTIONS = Object.freeze([
  { value: 'off', label: 'Off' },
  { value: 'instant', label: 'Instant' },
  { value: 'daily', label: 'Daily Summary' },
  { value: 'weekly', label: 'Weekly Summary' },
  { value: 'monthly', label: 'Monthly Summary' },
])

function NotificationSettings({ isOpen, onClose }) {
  const [{ success, error, actionLoading }, dispatch] = useReducer(messageReducer, {
    success: '', error: '', actionLoading: false,
  })
  
  const { 
    preferences, 
    loading: prefsLoading, 
    updateNotificationPreference, 
    resetToDefaults,
  } = useUserPreferences()

  // Memoized loading component - prevents re-creation
  const LoadingState = useMemo(() => (
    <ModalPortal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      animation="slideUp"
      zIndex="var(--z-modal)"
    >        <div className="text-center p-4">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading your notification settings...</p>
        </div>
    </ModalPortal>
  ), [isOpen, onClose])

  // Optimized notification change handler with auto-clear timeout
  const handleNotificationChange = useCallback(async (notificationType, value) => {
    dispatch({ type: 'CLEAR' })
    
    try {
      const result = await updateNotificationPreference(notificationType, value)
      if (result.success) {
        dispatch({ type: 'SET_SUCCESS', payload: 'Preference updated successfully!' })
        setTimeout(() => dispatch({ type: 'CLEAR' }), 3000)
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'Failed to update preference' })
      }
    } catch (err) {
      console.error('Error updating preference:', err)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update preference. Please try again.' })
    }
  }, [updateNotificationPreference])

  // Memoized alert component - prevents re-creation on every render
  const AlertMessage = useMemo(() => {
    if (!error && !success) return null
    
    const isError = !!error
    const message = error || success
    const styles = isError 
      ? { backgroundColor: 'var(--status-faults-bg)', color: 'var(--status-faults-text)' }
      : { backgroundColor: 'var(--status-running-bg)', color: 'var(--status-running-text)' }
    
    return (
      <div className={`alert ${isError ? 'alert-danger' : 'alert-success'}`} style={{ 
        padding: '12px 16px',
        fontSize: '0.875rem',
        borderRadius: '8px',
        border: 'none',
        marginBottom: '16px',
        ...styles,
      }}>
        <i className={`bi ${isError ? 'bi-exclamation-triangle' : 'bi-check-circle'} me-2`}></i>
        {message}
      </div>
    )
  }, [error, success])

  // Memoized notification setting component to prevent re-renders
  const NotificationSetting = useCallback(({ 
    id, 
    label, 
    description, 
    value, 
    notificationType,
  }) => (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between">
        <div className="flex-grow-1">
          <label className="form-label" htmlFor={id} style={{ 
            fontSize: '0.875rem', 
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '4px',
          }}>
            {label}
          </label>
          <div className="form-text" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {description}
          </div>
        </div>
        <div className="ms-4" style={{ minWidth: '180px' }}>
          <select 
            className="form-select"
            id={id}
            value={value}
            onChange={e => handleNotificationChange(notificationType, e.target.value)}
            style={{ 
              padding: '8px 12px', 
              fontSize: '0.875rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color-medium)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              backgroundColor: 'var(--bg-primary)',
            }}
          >
            {NOTIFICATION_OPTIONS.map(({ value: optValue, label: optLabel }) => (
              <option key={optValue} value={optValue}>{optLabel}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  ), [handleNotificationChange])

  // Memoized notification guide sidebar - prevents re-renders
  const NotificationGuide = useMemo(() => (
    <div className="col-12 col-lg-4 col-xl-3 mt-4 mt-lg-0">
      <div 
        className="card"
        style={{
          border: 'none',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-sm)',
          backgroundColor: 'var(--bg-light-blue)',
          height: 'fit-content',
        }}
      >
        <div className="card-body" style={{ padding: '16px' }}>
          <h6 className="card-title" style={{
            fontSize: '0.9rem',
            fontWeight: '600',
            color: 'var(--text-medium)',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
          }}>
            <i className="bi bi-lightbulb me-2" style={{ color: 'var(--color-focus-blue)' }}></i>
            Notification Guide
          </h6>

          <div className="mb-3">
            <strong style={{ fontSize: '0.8rem', color: 'var(--text-medium)' }}>Frequency Options:</strong>
            <ul style={{ 
              paddingLeft: '12px', 
              fontSize: '0.7rem',
              marginTop: '6px',
              marginBottom: '12px',
              lineHeight: '1.4',
            }}>
              {NOTIFICATION_OPTIONS.map(({ value, label }) => (
                <li key={value} style={{ marginBottom: '4px', color: 'var(--text-light-muted)' }}>
                  <strong style={{ color: 'var(--text-medium)' }}>{label}:</strong> {
                    value === 'off' ? 'No notifications' :
                    value === 'instant' ? 'Immediate alerts' :
                    value === 'daily' ? 'Daily summary' :
                    value === 'weekly' ? 'Weekly summary' :
                    'Monthly summary'
                  }
                </li>
              ))}
            </ul>
          </div>
          
          <div className="mb-2">
            <strong style={{ fontSize: '0.8rem', color: 'var(--text-medium)' }}>Device Alerts:</strong>
            <p style={{ 
              fontSize: '0.7rem', 
              lineHeight: '1.3',
              color: 'var(--text-light-muted)',
              marginTop: '3px',
              marginBottom: '8px',
            }}>
              Critical for monitoring device health. Instant frequency recommended.
            </p>
          </div>
          
          <div className="mb-0">
            <strong style={{ fontSize: '0.8rem', color: 'var(--text-medium)' }}>Configuration Updates:</strong>
            <p style={{ 
              fontSize: '0.7rem', 
              lineHeight: '1.3',
              color: 'var(--text-light-muted)',
              marginTop: '3px',
              marginBottom: '0',
            }}>
              Track system changes for audit purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  ), [])

  // Optimized reset handler with loading state
  const handleResetToDefaults = useCallback(async () => {
    dispatch({ type: 'CLEAR' })
    dispatch({ type: 'SET_LOADING', payload: true })

    try {
      const result = await resetToDefaults()
      if (result.success) {
        dispatch({ type: 'SET_SUCCESS', payload: 'Preferences reset to defaults successfully!' })
        setTimeout(() => dispatch({ type: 'CLEAR' }), 3000)
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'Failed to reset preferences' })
      }
    } catch (err) {
      console.error('Error resetting preferences:', err)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to reset preferences. Please try again.' })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [resetToDefaults])

  if (prefsLoading) return LoadingState

  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      animation="slideUp"
      zIndex="var(--z-modal)"
    >
      <div style={{ maxHeight: 'calc(100vh - 80px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Modal Header */}
        <div 
          style={{
            padding: '20px 24px 16px 24px',
            borderBottom: '1px solid var(--border-color-light-gray)',
            background: 'var(--bg-header-gradient)',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: 'var(--text-dark)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <i className="bi bi-bell"></i>
            Notification Settings
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--text-light-muted)',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.color = 'var(--text-medium)'
              e.target.style.backgroundColor = 'var(--color-hover-dark)'
            }}
            onMouseLeave={(e) => {
              e.target.style.color = 'var(--text-light-muted)'
              e.target.style.backgroundColor = 'transparent'
            }}
            aria-label="Close"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '24px',
          background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-light-blue) 100%)',
        }}>
          <div className="row">
            <div className="col-12 col-lg-8 col-xl-9">
              {AlertMessage}

              <div className="mb-4">
                <h4 className="mb-3" style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: 'var(--text-medium)',
                  borderBottom: '1px solid var(--border-color-light-gray)',
                  paddingBottom: '8px',
                }}>
                  <i className="bi bi-gear me-2"></i>
                  Notification Types
                </h4>
                <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                  Configure when you receive notifications. All active notifications are delivered via in-app alerts and email automatically.
                </p>

                <NotificationSetting
                  id="deviceAlertsFreq"
                  label="Device Alerts"
                  description="Offline devices, performance issues, faults"
                  value={preferences.notifications.deviceAlerts}
                  notificationType="deviceAlerts"
                />

                <NotificationSetting
                  id="configUpdatesFreq"
                  label="Configuration Updates"
                  description="Settings changes, firmware updates"
                  value={preferences.notifications.configUpdates}
                  notificationType="configUpdates"
                />

                <div style={{
                  padding: '12px 16px',
                  backgroundColor: 'var(--alert-info-light-bg)',
                  border: '1px solid var(--alert-info-light-border)',
                  borderRadius: '8px',
                  marginBottom: '24px',
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle text-primary me-2"></i>
                    <small style={{ color: 'var(--alert-info-text)', fontSize: '0.875rem' }}>
                      <strong>Delivery Methods:</strong> All active notifications (except &quot;Off&quot;) are automatically sent via in-app alerts and email to your registered address.
                    </small>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-center">
                <button 
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleResetToDefaults}
                  disabled={actionLoading}
                  style={{
                    padding: '12px 24px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color-strong)',
                    color: 'var(--text-medium)',
                    backgroundColor: 'var(--bg-secondary)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {actionLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Resetting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-counterclockwise me-2"></i>
                      Reset to Defaults
                    </>
                  )}
                </button>
              </div>
            </div>

            {NotificationGuide}
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}

NotificationSettings.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default NotificationSettings
